import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { GraduationCap, BookOpen, Star, MapPin } from 'lucide-react';
import { education } from '../../data/constants';


const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 100px 20px;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
`;

const Header = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 52px;
  font-weight: 800;
  color: #FFFFFF;
  letter-spacing: -0.02em;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Desc = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 600px;
  line-height: 1.6;
  margin: 0;
`;

const EducationList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const EduItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 40px;
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const YearNode = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  
  .year {
    font-size: 18px;
    font-weight: 800;
    color: #854CE6;
  }
  
  .duration {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(133, 76, 230, 0.3);
    transform: translateX(10px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const InstitutionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InstName = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #FFF;
  margin: 0;
`;

const DegreeText = styled.div`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.div`
  background: linear-gradient(135deg, rgba(133, 76, 230, 0.2) 0%, rgba(133, 76, 230, 0.1) 100%);
  border: 1px solid rgba(133, 76, 230, 0.2);
  padding: 8px 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    font-size: 14px;
    font-weight: 700;
    color: #854CE6;
  }
`;

const Details = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  margin: 0;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
`;

const InstLogo = styled.img`
  width: 60px;
  height: 60px;
  background-color: #000;
  border-radius: 12px;
  object-fit: contain;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Education = () => {
  return (
    <Container id="education">
      <Wrapper>
        <Header
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Title>Academic Journey</Title>
          <Desc>
            A narrative of foundational learning and technical specialization that fuels my product-first engineering perspective.
          </Desc>
        </Header>

        <EducationList>
          {education.map((edu, index) => (
            <EduItem 
              key={index}
              as={motion.div}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <YearNode>
                <div className="year">2021-25</div>
                <div className="duration">Foundational</div>
              </YearNode>

              <Card>
                <CardHeader>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <InstLogo src={edu.img} alt={edu.school} />
                    <InstitutionInfo>
                      <InstName>{edu.school}</InstName>
                      <DegreeText>
                        <GraduationCap size={18} color="#FFD700" />
                        {edu.degree}
                      </DegreeText>
                    </InstitutionInfo>
                  </div>
                  <Badge>
                    <Star size={16} fill="#FFA500" color="#FFA500" />
                    <span>8.3 GPA</span>
                  </Badge>
                </CardHeader>

                <Details>{edu.desc}</Details>

                <MetaGrid>
                  <MetaItem>
                    <MapPin size={16} color="#FF4B4B" />
                    Jaipur, Rajasthan
                  </MetaItem>
                  <MetaItem>
                     <BookOpen size={16} color="#00C851" />
                     Computer Science & Engineering
                  </MetaItem>
                </MetaGrid>
              </Card>
            </EduItem>
          ))}
        </EducationList>
      </Wrapper>
    </Container>
  );
};

export default Education;